import { Type, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ForeignKeyConfig } from '../field-definition';

export type ForeignKeyListLoader<TService> = (
  service: TService,
  query: any,
  config?: any
) => Observable<any>;

/**
 * Helper to create FK config for standard getList endpoints.
 *
 * @example
 * foreignKey: createStandardFkConfig(BrandService)
 * foreignKey: createStandardFkConfig(BrandService, { maxResultCount: 500 }, 'supplierName')
 */
export function createStandardFkConfig<TService>(
  serviceType: Type<TService>,
  query?: any,
  displayField: string = 'name',
  keyField: string = 'id',
  listLoader?: ForeignKeyListLoader<TService>
): ForeignKeyConfig {
  const defaultQuery = { maxResultCount: 11, skipCount: 0 };
  let service: TService | null = null;
  const cache = new Map<string, Observable<any[]>>();
  const itemCache = new Map<string, Observable<any>>();
  const itemsByIdsCache = new Map<string, Observable<any[]>>();
  const resolvedItemsCache = new Map<string, any>();

  const cacheResolvedItem = (item: any) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const key = item[keyField];
    if (key === null || key === undefined) {
      return;
    }

    resolvedItemsCache.set(String(key), item);
  };

  const resolveService = () => {
    if (!service) {
      service = inject(serviceType);
    }
    return service;
  };

  const normalizeListResponse = (response: any, includeTotalCount: boolean) => {
    if (includeTotalCount || Array.isArray(response)) {
      return response;
    }

    return response?.items ?? [];
  };

  const getOrCreateCachedRequest = <T>(
    requestCache: Map<string, Observable<T>>,
    cacheKey: string,
    requestFactory: () => Observable<T>
  ) => {
    const cachedRequest = requestCache.get(cacheKey);
    if (cachedRequest) {
      return cachedRequest;
    }

    const request$ = requestFactory();
    requestCache.set(cacheKey, request$);
    return request$;
  };

  return {
    loader: (forceRefresh?: boolean, queryOverride?: any, config?: any) => {
      // First time: inject service (must be called from injection context)
      const svc = resolveService();

      const finalQuery = {
        ...defaultQuery,
        ...(query),
        ...queryOverride,
      };
      const cacheKey = JSON.stringify({
        query: finalQuery,
        includeTotalCount: !!config?.includeTotalCount,
      });

      if (forceRefresh) {
        cache.clear();
      }

      return getOrCreateCachedRequest(cache, cacheKey, () =>
        (listLoader
          ? listLoader(svc, finalQuery, config)
          : (svc as any).getList(finalQuery, config)
        ).pipe(
          map((response: any) => normalizeListResponse(response, !!config?.includeTotalCount)),
          shareReplay(1)
        )
      );
    },
    getById: (id: string | number, config?: any) => {
      const idKey = String(id);
      if (config?.forceRefresh) {
        itemCache.delete(idKey);
      }

      const svc = resolveService() as any;
      if (typeof svc.get !== 'function') {
        throw new TypeError('FK service does not expose get(id)');
      }

      return getOrCreateCachedRequest(itemCache, idKey, () =>
        svc.get(id, config).pipe(
          tap((item: any) => cacheResolvedItem(item)),
          shareReplay(1)
        )
      );
    },
    getByIds: (ids: Array<string | number>, config?: any) => {
      const { forceRefresh, ...restConfig } = config ?? {};
      const serializedIds = ids.map(String).join(',');
      const cacheKey = JSON.stringify({
        ids: ids.map(String).sort((a, b) => a.localeCompare(b)),
        config: restConfig,
      });

      if (forceRefresh) {
        itemsByIdsCache.delete(cacheKey);
      }

      const svc = resolveService() as any;
      if (typeof svc.getListByIds !== 'function') {
        throw new TypeError('FK service does not expose getListByIds(input)');
      }

      return getOrCreateCachedRequest(itemsByIdsCache, cacheKey, () =>
        svc.getListByIds({ ids: serializedIds } as any, restConfig).pipe(
            map((response: any) =>
              Array.isArray(response) ? response : response?.items ?? []
            ),
            tap((items: any[]) => items.forEach(cacheResolvedItem)),
            shareReplay(1)
          )
      );
    },
    getCachedItem: (id: string | number) => resolvedItemsCache.get(String(id)),
    displayField,
    keyField,
  };
}
